"use client";

import React, { useState } from "react";
import { m, AnimatePresence } from "motion/react";
import { IconChevronDown } from "@tabler/icons-react";
import { cn } from "@/lib/utils";

const faqs = [
    {
        question: "A Midas atende qualquer tipo de negócio?",
        answer: "Sim. Nossa expertise cobre desde pequenas empresas em fase de crescimento até grandes corporações que precisam escalar. Já trabalhamos com saúde, varejo, construção, educação, tecnologia, alimentação, serviços e muito mais. O Método D'Or foi desenvolvido justamente para se adaptar a qualquer segmento.",
    },
    {
        question: "Quanto tempo leva para ver os primeiros resultados?",
        answer: "Depende da etapa em que seu negócio está e das ações contratadas. Campanhas de tráfego pago costumam gerar resultados nas primeiras semanas. Estratégias de posicionamento e marca têm impacto mais robusto a partir do 2º ou 3º mês. Desde o dia 1, você tem acesso ao nosso dashboard e acompanha cada número em tempo real.",
    },
    {
        question: "Preciso contratar todas as soluções ou posso começar com uma parte?",
        answer: "Você começa pelo que faz mais sentido para o momento do seu negócio. Muitos clientes entram pelo tráfego pago ou pelas páginas e vão expandindo conforme os resultados aparecem. Nossa recomendação inicial é baseada em um diagnóstico completo e gratuito que fazemos antes de fechar qualquer contrato.",
    },
    {
        question: "Como funcionam as parcerias com TV e outdoors?",
        answer: "Temos uma rede de parceiros em emissoras regionais e nacionais, além de empresas de OOH (outdoor, busdoor, painéis digitais) espalhadas pelo Brasil inteiro. Cuidamos de tudo: planejamento de mídia, produção do material e negociação de espaços. Você só aprova e aguarda os resultados.",
    },
    {
        question: "Vocês trabalham com contratos longos ou posso sair quando quiser?",
        answer: "Trabalhamos com contratos mensais sem fidelidade forçada. Nosso principal mecanismo de retenção são os resultados que entregamos. Se não estamos performando, você pode encerrar com aviso prévio de 30 dias. Simples assim.",
    },
    {
        question: "O que é exatamente o Método D'Or?",
        answer: "É um framework proprietário que cobre as 5 etapas do ciclo de crescimento de qualquer negócio: Descoberta (diagnóstico e estratégia), Atração (geração de demanda), Consideração (nutrição e qualificação), Ação (conversão e vendas) e Apologia (retenção e indicação). Cada etapa tem KPIs próprios e é gerenciada por especialistas dedicados.",
    },
];

function FaqItem({
    faq,
    isOpen,
    onClick,
}: {
    faq: (typeof faqs)[0];
    isOpen: boolean;
    onClick: () => void;
}) {
    return (
        <div
            className={cn(
                "border-b border-neutral-800 last:border-0 transition-colors duration-300",
                isOpen && "bg-neutral-900/50"
            )}
        >
            <button
                onClick={onClick}
                className="w-full flex items-center justify-between py-6 px-4 md:px-8 text-left focus:outline-none group"
            >
                <span
                    className={cn(
                        "text-lg md:text-xl font-semibold transition-colors duration-300",
                        isOpen ? "text-midas-yellow-500" : "text-white group-hover:text-midas-yellow-400"
                    )}
                >
                    {faq.question}
                </span>
                <span
                    className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300",
                        isOpen
                            ? "bg-midas-yellow-500 text-black rotate-180"
                            : "bg-neutral-800 text-midas-yellow-500 group-hover:bg-neutral-700"
                    )}
                >
                    <IconChevronDown size={20} stroke={2.5} />
                </span>
            </button>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <m.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <p className="px-4 md:px-8 pb-8 text-neutral-400 leading-relaxed text-base md:text-lg">
                            {faq.answer}
                        </p>
                    </m.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function FaqSection() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    return (
        <section className="py-32 bg-black relative border-t border-neutral-900 overflow-hidden">
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-midas-yellow-500/[0.02] blur-[150px] rounded-full pointer-events-none" />
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.03] pointer-events-none" />

            <div className="max-w-5xl mx-auto px-6 relative z-10">
                <div className="text-center mb-16 md:mb-24">
                    <span className="text-midas-yellow-500 font-mono text-xs tracking-[4px] uppercase mb-4 block">
                        Dúvidas Frequentes
                    </span>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                        Respondendo suas{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-midas-yellow-400 to-amber-500">
                            perguntas
                        </span>
                    </h2>
                </div>

                <div className="bg-neutral-950/80 backdrop-blur-md border border-neutral-800 rounded-3xl overflow-hidden shadow-2xl">
                    {faqs.map((faq, index) => (
                        <FaqItem
                            key={index}
                            faq={faq}
                            isOpen={openIndex === index}
                            onClick={() => setOpenIndex(openIndex === index ? null : index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
